import React from "react";
import { useState, useEffect } from "react";
import { Box, Paper, useTheme, IconButton } from '@mui/material';
import Checkbox from "../../../components/controls/Checkbox";
import { tokens } from "../../../assets/theme";
import { useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import messages from "./messages";
import AxionControls from "../../../components/controls/AxionControls";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import { DataGridPro, useGridApiRef } from "@mui/x-data-grid-pro";
import { svSE, enUS } from '@mui/x-data-grid/locales';

export default function AutocodeSchedulesGrid(props) {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const navigate = useNavigate();
    const apiRef = useGridApiRef();
    const language = localStorage.getItem('language') || 'en';
    const translations = language === "sv" ? svSE : enUS;
    const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
    const [sortModelStorage, setSortModelStorage] = useState([
        { field: "name", sort: "asc" }
    ]);
    const { formatMessage } = useIntl();

    function calculateEndTime(startTimeStr, lengthInMinutes) {
        if (!startTimeStr || isNaN(lengthInMinutes)) return "";

        const [hours, minutes, seconds] = startTimeStr.split(":").map(Number);
        // Use a fixed anchor date to see if the end time crosses into the next day
        const start = new Date(2000, 0, 1, hours, minutes, seconds);
        const end = new Date(start.getTime() + lengthInMinutes * 60000);

        const endHours = end.getHours().toString().padStart(2, '0');
        const endMinutes = end.getMinutes().toString().padStart(2, '0');
        const endSeconds = end.getSeconds().toString().padStart(2, '0');

        const isNextDay = end.getDate() > start.getDate();
        const isTwoDaysLater = end.getDate() > start.getDate() + 1;
        const isThreeDaysLater = end.getDate() > start.getDate() + 2;

        return `${endHours}:${endMinutes}${isThreeDaysLater ? " +3" : isTwoDaysLater ? " +2" : isNextDay ? " +1" : ""}`;
    }

    const [columns, setColumns] = useState([
        {
            field: "name",
            headerName: formatMessage(messages.nameColumn),
            flex: 1,
        },
        {
            field: "stopReasonCodeId",
            headerName: formatMessage(messages.autocodeStopReasonCodeColumn),
            flex: 1,
            renderCell: (params) => {
                const stopReasonCode = props.stopReasonCodes?.find(x => x.id === params.value);
                return <>{stopReasonCode ? stopReasonCode.name : "Unknown"}</>
            }
        },
        {
            field: "startTime",
            headerName: formatMessage(messages.startTimeColumn),
            flex: 1,
            renderCell: (timeSpanStr) => {
                if (!timeSpanStr || !timeSpanStr.value) return "";
                const [hours, minutes] = timeSpanStr.value.split(':');
                return `${String(hours).padStart(2, '0')}:${minutes}`;
            }
        },
        {
            field: "lengthInMinutes",
            headerName: formatMessage(messages.lengthInMinutesColumn),
            flex: 1,
            renderCell: (params) => {
                const endTime = calculateEndTime(params.row.startTime, params.value);
                return <>{endTime} ({params.value} min)</>
            }
        },
        {
            field: "mondayActive",
            headerName: formatMessage(messages.mondayActive),
            flex: 0.5,
            renderCell: (params) => (
                <>
                    <Checkbox
                        id="mondayActive"
                        name="mondayActive"
                        label=""
                        value={params.value}
                        disabled={true}
                        onChange={() => { }}
                    />
                </>
            ),
        },
        {
            field: "tuesdayActive",
            headerName: formatMessage(messages.tuesdayActive),
            flex: 0.5,
            renderCell: (params) => (
                <>
                    <Checkbox
                        id="tuesdayActive"
                        name="tuesdayActive"
                        label=""
                        value={params.value}
                        disabled={true}
                        onChange={() => { }}
                    />
                </>
            ),
        },
        {
            field: "wednesdayActive",
            headerName: formatMessage(messages.wednesdayActive),
            flex: 0.5,
            renderCell: (params) => (
                <>
                    <Checkbox
                        id="wednesdayActive"
                        name="wednesdayActive"
                        label=""
                        value={params.value}
                        disabled={true}
                        onChange={() => { }}
                    />
                </>
            ),
        },
        {
            field: "thursdayActive",
            headerName: formatMessage(messages.thursdayActive),
            flex: 0.5,
            renderCell: (params) => (
                <>
                    <Checkbox
                        id="thursdayActive"
                        name="thursdayActive"
                        label=""
                        value={params.value}
                        disabled={true}
                        onChange={() => { }}
                    />
                </>
            ),
        },
        {
            field: "fridayActive",
            headerName: formatMessage(messages.fridayActive),
            flex: 0.5,
            renderCell: (params) => (
                <>
                    <Checkbox
                        id="fridayActive"
                        name="fridayActive"
                        label=""
                        value={params.value}
                        disabled={true}
                        onChange={() => { }}
                    />
                </>
            ),
        },
        {
            field: "saturdayActive",
            headerName: formatMessage(messages.saturdayActive),
            flex: 0.5,
            renderCell: (params) => (
                <>
                    <Checkbox
                        id="saturdayActive"
                        name="saturdayActive"
                        label=""
                        value={params.value}
                        disabled={true}
                        onChange={() => { }}
                    />
                </>
            ),
        },
        {
            field: "sundayActive",
            headerName: formatMessage(messages.sundayActive),
            flex: 0.5,
            renderCell: (params) => (
                <>
                    <Checkbox
                        id="sundayActive"
                        name="sundayActive"
                        label=""
                        value={params.value}
                        disabled={true}
                        onChange={() => { }}
                    />
                </>
            ),
        },
        {
            field: "splitOngoingStopOnAutocodeStart",
            headerName: formatMessage(messages.splitStart),
            flex: 0.75,
            renderCell: (params) => (
                <>
                    <Checkbox
                        id="splitOngoingStopOnAutocodeStart"
                        name="splitOngoingStopOnAutocodeStart"
                        label=""
                        value={params.value}
                        disabled={true}
                        onChange={() => { }}
                    />
                </>
            ),
        },
        {
            field: "splitOngoingStopOnAutocodeEnd",
            headerName: formatMessage(messages.splitEnd),
            flex: 0.75,
            renderCell: (params) => (
                <>
                    <Checkbox
                        id="splitOngoingStopOnAutocodeEnd"
                        name="splitOngoingStopOnAutocodeEnd"
                        label=""
                        value={params.value}
                        disabled={true}
                        onChange={() => { }}
                    />
                </>
            ),
        },
        {
            field: "edit",
            headerName: "",
            renderCell: (params) => {
                return (
                    <>
                        <IconButton
                            aria-label="Edit schedule"
                            size="small"
                            sx={{
                                "&:hover": {
                                    backgroundColor: colors.primary[500],
                                },
                                backgroundColor: colors.primary[700],
                            }}
                            onClick={() => {
                                props.handleEditClick(params.row.id)
                            }}
                        >
                            <RemoveRedEyeOutlinedIcon fontSize="inherit" />
                        </IconButton>
                        <IconButton
                            aria-label="Delete schedule"
                            size="small"
                            sx={{
                                "&:hover": {
                                    backgroundColor: colors.primary[500],
                                },
                                backgroundColor: colors.primary[700],
                                marginLeft: "10px"
                            }}
                            onClick={() => {
                                props.handleDeleteClick(params.row.id)
                            }}
                        >
                            <DeleteIcon fontSize="inherit" />
                        </IconButton>
                    </>
                )
            },
            width: 90,
        },
    ]);

    const columnOrderLocalStorageName = 'autocode-schedules-columnOrder';
    const columnWidthsLocalStorageName = 'autocode-schedules-columnWidths';

    // Load persisted column order, widths, and sort order on mount
    useEffect(() => {
        let columnsToFix = columns;
        // Load column order
        const savedColumnOrder = localStorage.getItem(columnOrderLocalStorageName);
        if (savedColumnOrder) {
            const orderArray = JSON.parse(savedColumnOrder);
            columnsToFix = reorderColumns(orderArray, columnsToFix);
        }

        // Load column widths
        const savedWidths = localStorage.getItem(columnWidthsLocalStorageName);
        if (savedWidths) {
            const widthMap = JSON.parse(savedWidths);
            columnsToFix = applyColumnWidths(widthMap, columnsToFix);
        }
        setColumns(columnsToFix);
    }, []);


    // Reorder columns based on saved order
    const reorderColumns = (orderArray, columnsToFix) => {
        const reorderedColumns = orderArray.map((field) =>
            columnsToFix.find((col) => col.field === field)
        );
        return reorderedColumns;
    };

    // Apply saved column widths
    const applyColumnWidths = (widthMap, columnsToFix) => {
        const updatedColumns = columnsToFix.map((col) => ({
            ...col,
            width: widthMap[col.field] || col.width,
            flex: widthMap[col.field] !== undefined ? undefined : col.flex,
        }));
        return updatedColumns;
    };

    // Handle column order change
    const handleColumnOrderChange = () => {
        if (!apiRef.current) {
            return;
        }
        const orderedColumns = apiRef.current.getVisibleColumns();
        const newColumnOrder = orderedColumns.map((col) => col.field);
        localStorage.setItem(columnOrderLocalStorageName, JSON.stringify(newColumnOrder));
    };

    // Handle column resize
    const handleColumnResize = (params) => {
        const { colDef, width } = params;
        const newWidths = JSON.parse(localStorage.getItem(columnWidthsLocalStorageName)) || {};
        newWidths[colDef.field] = width;
        localStorage.setItem(columnWidthsLocalStorageName, JSON.stringify(newWidths));
    };

    if (props.autocodeSchedules?.isLoading) {
        return <AxionControls.AxionBackdrop open={true} />
    }

    return (
        <Box padding={2} paddingTop={8}>
            <Paper
                sx={{
                    position: "relative",
                    borderRadius: "4px",
                }}
            >
                <Box
                    m="0px 0 0 0"
                    height="76vh"
                    sx={{
                        "& .MuiDataGrid-root": {
                            backgroundColor: colors.primary[400],
                            borderColor: `${colors.primary[700]} !important`,
                            borderRadius: "0px",
                            fontSize: "1.0rem",
                            border: "0 !important",
                        },
                        "& .MuiDataGrid-main": {
                            borderRadius: "0px",
                            border: "0 !important",
                            borderColor: `${colors.primary[700]} !important`,
                        },
                        "& .MuiDataGrid-cell": {
                            borderColor: `${colors.primary[700]} !important`,
                            borderWidth: "2px !important",
                        },
                        "& .name.column--cell": {
                            color: colors.greenAccent[300],
                        },
                        "& .MuiDataGrid-columnHeaders": {
                            backgroundColor: colors.primary[400],
                            fontSize: '1.0rem',
                            borderColor: `${colors.primary[700]} !important`,
                            borderWidth: "2px"
                        },
                        "& .MuiDataGrid-virtualScroller": {
                            backgroundColor: colors.primary[400],
                        },
                        "& .MuiDataGrid-footerContainer": {
                            borderTop: "none",
                            backgroundColor: colors.primary[600],
                        },
                        "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
                            color: `${colors.gray[100]} !important`,
                        },
                        "& .super-app-theme--11": {
                            bgcolor: `${colors.redAccent[500]} !important`,
                        },
                        "& .super-app-theme--undefined-stop": {
                            bgcolor: `${colors.redAccent[500]} !important`,
                        },
                        "& .MuiDataGrid-columnHeader .MuiDataGrid-menuIconButton": {
                            opacity: 0,
                            transition: "opacity 0.2s"
                        },

                        "& .MuiDataGrid-columnHeader:hover .MuiDataGrid-menuIconButton": {
                            opacity: 1
                        },
                    }}
                >
                    {(!props.autocodeSchedules?.isLoading) && <DataGridPro
                        apiRef={apiRef}
                        localeText={translations.components.MuiDataGrid.defaultProps.localeText}
                        onColumnResize={handleColumnResize}
                        onColumnOrderChange={handleColumnOrderChange}
                        rows={props.autocodeSchedules?.data}
                        sortModel={sortModelStorage}
                        onSortModelChange={(newModel) => setSortModelStorage(newModel)}
                        columns={columns}
                        columnVisibilityModel={columnVisibilityModel}
                        onColumnVisibilityModelChange={(newModel) =>
                            setColumnVisibilityModel(newModel)
                        }
                        slotProps={{
                            panel: {
                                sx: {
                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                        backgroundColor: "#799c4a",
                                        opacity: 0.3,
                                    },
                                    "& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb": {
                                        backgroundColor: "#799c4a",
                                    },
                                    "& .MuiDataGrid-panelFooter .MuiButtonBase-root": {
                                        color: colors.primary[200],
                                        fontSize: "0.75rem",
                                        "&:hover": {
                                            color: colors.primary[300],
                                        },
                                    },
                                    "& .MuiFormLabel-root.Mui-focused": {
                                        color: colors.primary[200],
                                    },
                                },
                            }
                        }}
                    />}
                </Box>
            </Paper>
        </Box>
    );
}